import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOMeta from "@/components/feature/SEOMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory, articles } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = getCategoryBySlug(slug || "");

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = getArticlesByCategory(category.id);
  const allArticles = articles.slice(0, 8);

  const breadcrumbItems = [{ label: category.name }];

  const pageJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 探花系列精品聚合平台`,
    description: category.metaDescription,
    url: `http://lalahzz.cn/category/${category.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "探花系列精品聚合平台",
      url: "http://lalahzz.cn/",
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://lalahzz.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category.name,
        item: `http://lalahzz.cn/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} - 探花系列精品聚合平台`}
        description={category.metaDescription}
        keywords={`${category.name},探花系列,探花精选,国内探花,探花91`}
        canonical={`http://lalahzz.cn/category/${category.slug}`}
        ogType="website"
        jsonLd={[pageJsonLd, breadcrumbJsonLd]}
      />
      <Navbar />

      <main>
        {/* Category Header */}
        <section className="relative pt-24 md:pt-28 pb-12 md:pb-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-xl">
                  {category.description}
                </p>
              </div>
              <span className="text-sm text-foreground-500 bg-background-200/70 px-3 py-1.5 rounded-full whitespace-nowrap">
                共 {category.count} 部作品
              </span>
            </div>
          </div>
        </section>

        {/* Sub Categories / Featured */}
        <section className="py-10 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              热门推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.slice(0, 3).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-colors"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                        {article.tag}
                      </span>
                      <span className="text-xs text-foreground-500">
                        {article.date}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
              {categoryArticles.length === 0 &&
                allArticles.slice(0, 3).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-colors"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                          {article.tag}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {article.date}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="py-10 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                全部内容
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {(categoryArticles.length > 0
                ? categoryArticles
                : allArticles
              ).map((article) => (
                <article
                  key={article.id}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="text-xs font-medium text-background-50 bg-primary-500 px-2.5 py-1 rounded-full">
                          {article.tag}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-xs text-background-300">
                          {article.date}
                        </span>
                        <h3 className="text-sm font-bold text-background-50 mt-1 line-clamp-2">
                          {article.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Related Categories */}
        <section className="py-10 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              其他分类
            </h2>
            <div className="flex flex-wrap gap-3">
              {[
                { name: "国内探花", slug: "domestic" },
                { name: "探花精选", slug: "selection" },
                { name: "探花91", slug: "tanhua91" },
                { name: "探花直播", slug: "live" },
                { name: "顶级大师", slug: "top-master" },
                { name: "探花一区", slug: "zone1" },
              ]
                .filter((c) => c.slug !== category.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}